import React, { type JSX } from "react";

import {
    IBooleanField,
    ValidationTypes,
} from "../models/formFields.interfaces";
import { notEmpty, unique } from "../utils/functional";
import { IFormFieldState } from "./AbstractFormField";
import { AbstractInput } from "./AbstractInput";
import { runBooleanValidator } from "./validation";

// TODO(ssr, LEVEL 5 out of 10): Migrate to functional component
// Complexity: Checkbox input with componentDidUpdate lifecycle to trigger validation when checked
// state changes. Custom runValidators() override for boolean-specific validation using
// runBooleanValidator(). Overrides buildLabel() to return null because checkbox labels wrap the input
// instead of being adjacent. Renders label wrapping input with optional HTML content via
// dangerouslySetInnerHTML.
// Migration: Replace componentDidUpdate with useEffect watching props.checked. Extract boolean
// validation logic into useBooleanFieldValidation hook. Use useFormField hook (from migrated
// AbstractFormField) for base functionality. Replace inputElem ref with useRef. The label wrapping
// pattern becomes a simple conditional render. Main work is adapting lifecycle and validation to hooks.
export class FormCheckbox<T extends string> extends AbstractInput<
    T,
    IBooleanField<T>,
    IFormFieldState
> {
    static defaultProps = {
        type: "checkbox",
    };

    state: IFormFieldState = {
        focused: false,
    };

    public componentDidUpdate(prevProps: IBooleanField<T>): void {
        if (prevProps.checked !== this.props.checked) {
            this.runValidators();
        }
    }

    public runValidators(): string[] {
        if (!this.props.validation) {
            return [];
        }
        const value = this.props.checked || false;
        const validators = ([] as ValidationTypes[]).concat(
            this.props.validation,
        );

        const errors = validators
            .filter(unique)
            .map((validator) => {
                return runBooleanValidator(validator, value);
            })
            .filter(notEmpty);
        if (this.props.onValidStateChange) {
            this.props.onValidStateChange(this.props.name, errors);
        }
        return errors;
    }

    protected buildLabel(): JSX.Element | null {
        // Override this because checkbox labels wrap the input, instead of getting placed adjacent to it.
        return null;
    }

    protected buildControl(): React.JSX.Element {
        const props = { ...this.getInputProps(), checked: this.props.checked };
        props.type = props.type || "checkbox";
        return (
            <label className={this.props.labelCSSClass}>
                <input
                    ref={(ref) => {
                        this.inputElem = ref;
                    }}
                    {...props}
                />
                {this.props.label && this.props.hasHTML && (
                    <span
                        dangerouslySetInnerHTML={{ __html: this.props.label }}
                    />
                )}
                {!this.props.hasHTML ? this.props.label : null}
            </label>
        );
    }
}
